import type { ReactNode } from 'react';
import { Navigate } from 'react-router';

import {
  ProviderStatus,
  useBooleanFlagValue,
  useOpenFeatureClientStatus,
} from '@openfeature/react-sdk';

import { type FeatureFlag, featureFlags } from '@/shared/config/feature-flags';
import { type KnownOpenFeatureFlag } from '@/shared/config/openfeature-flags';
import { ROUTES } from '@/shared/config/routes';

/** At least one flag. Makes "a list that gates nothing" unrepresentable. */
type NonEmptyFlagList = readonly [KnownOpenFeatureFlag, ...KnownOpenFeatureFlag[]];

/**
 * The feature-flag guard supports BOTH systems during the migration window:
 *
 *   - `provider: 'openfeature'` (default) → reads the flag through
 *     `useBooleanFlagValue` and the Statsig-backed OpenFeature stack.
 *     `flag` must be one of `KNOWN_OPENFEATURE_FLAGS`.
 *
 *   - `provider: 'diy'` → reads the flag through the legacy
 *     localStorage-based `featureFlags.read(...)` registry. `flag` must
 *     be one of the `FEATURE_FLAGS` keys still living in
 *     `shared/config/feature-flags.ts`. New consumers should NOT pick
 *     this branch — it exists only so flags whose owners have not
 *     migrated yet keep working.
 *
 * The discriminated union keeps callers honest: TypeScript rejects mixing
 * a DIY flag key with the OpenFeature provider (and vice-versa).
 */
type FeatureFlagGuardProps =
  | {
      provider?: 'openfeature';
      /**
       * A list is satisfied by ANY of its flags. For a route whose page hosts several
       * independently rolled-out features: the guard only decides whether the page is
       * reachable, and each feature inside it still gates on its own flag.
       */
      flag: KnownOpenFeatureFlag | NonEmptyFlagList;
      defaultValue?: boolean;
      /**
       * Invert polarity: redirect fires while the raw flag value is ON.
       * Use for hide-when-on kill switches (e.g. `hide_asset_approvals`), so the
       * default (off) preserves today's behaviour.
       *
       * With a list, the negation applies to the ANY-of result as a whole, so children
       * render only while NONE of the flags is on.
       */
      invert?: boolean;
      /** Where to send the user when the flag is off. Defaults to home. Supports relative paths (`".."`). */
      redirectTo?: string;
      children: ReactNode;
    }
  | {
      provider: 'diy';
      flag: FeatureFlag;
      defaultValue?: boolean;
      invert?: boolean;
      /** Where to send the user when the flag is off. Defaults to home. Supports relative paths (`".."`). */
      redirectTo?: string;
      children: ReactNode;
    };

/**
 * Evaluates one flag per instance and recurses through the rest, carrying the running result
 * down so the innermost instance renders the outcome.
 *
 * One `useBooleanFlagValue` per instance is what allows any number of flags: the hook count per
 * component is constant, so a list whose length changes between renders cannot break the rules
 * of hooks. It has to be that hook rather than a single imperative read off
 * `useOpenFeatureClient`, because only the per-flag hook re-renders when a flag's VALUE changes
 * - `useOpenFeatureClientStatus` subscribes to the same events but stores the provider status,
 * so a configuration change that leaves the status READY sets the same value and React bails.
 *
 * The chain deliberately does NOT stop at the first enabled flag. Short-circuiting would render
 * `children` at whatever depth first matched, so a flag flipping on ABOVE the matching one would
 * move them and React would unmount the guarded page mid-session. Always descending keeps
 * `children` at a fixed depth, and costs only the evaluation of flags already being watched.
 */
const AnyFlagEnabled = ({
  flag,
  rest,
  defaultValue,
  invert = false,
  anyEnabled = false,
  whenNone,
  children,
}: {
  flag: KnownOpenFeatureFlag;
  rest: readonly KnownOpenFeatureFlag[];
  defaultValue?: boolean;
  /** Negates the whole any-of result, applied once at the end of the chain. */
  invert?: boolean;
  /** Whether any flag already evaluated further up the chain was enabled. */
  anyEnabled?: boolean;
  /** Rendered once every flag has been evaluated and none was enabled. */
  whenNone: ReactNode;
  children: ReactNode;
}) => {
  // OpenFeature has its own boolean default contract, so a `false` fallback is safe here and
  // matches the pre-existing behaviour.
  const isEnabled = useBooleanFlagValue(flag, defaultValue ?? false) || anyEnabled;

  if (rest.length > 0) {
    return (
      <AnyFlagEnabled
        flag={rest[0]}
        rest={rest.slice(1)}
        defaultValue={defaultValue}
        invert={invert}
        anyEnabled={isEnabled}
        whenNone={whenNone}
      >
        {children}
      </AnyFlagEnabled>
    );
  }

  return <>{(invert ? !isEnabled : isEnabled) ? children : whenNone}</>;
};

/**
 * Route guard that redirects to home when the requested feature flag is
 * disabled. Use this to protect routes that are only accessible to
 * testers or to gradual rollouts.
 *
 * For legacy DIY flags, use the `featureFlags` console helpers:
 *   featureFlags.enable('PERSONA_DETAIL_V2_ENABLED')
 *   featureFlags.disable('PERSONA_DETAIL_V2_ENABLED')
 *   featureFlags.reset('PERSONA_DETAIL_V2_ENABLED')
 */
export const FeatureFlagGuard = (props: FeatureFlagGuardProps) => {
  // Read the caller-supplied default BEFORE applying any local default.
  // For the DIY branch we MUST forward `undefined` when the caller did
  // not opt in — `featureFlags.read` does `defaultValue ?? FEATURE_FLAGS[name]`,
  // and the nullish-coalescing operator does NOT short-circuit on
  // `false`. If we passed our own `false` fallback here, the registered
  // default in `FEATURE_FLAGS` would never be consulted (silently
  // shadowed for any future flag whose default is `true`).
  const explicitDefault = props.defaultValue;

  // While the provider re-evaluates after a context change (e.g. the deep-link
  // seam's silent workspace switch → Statsig `updateUserAsync`), a cold cache
  // serves the flag's DEFAULT — redirecting on that transient value destroys
  // the deep link the user just opened. Hold until the evaluation settles;
  // only a settled `false` may redirect.
  const providerStatus = useOpenFeatureClientStatus();
  const isReconciling = props.provider !== 'diy' && providerStatus === ProviderStatus.RECONCILING;
  const whenNone = isReconciling ? null : <Navigate to={props.redirectTo ?? ROUTES.HOME} replace />;

  if (props.provider === 'diy') {
    const rawValue = featureFlags.read(props.flag, explicitDefault);
    return (props.invert ? !rawValue : rawValue) ? <>{props.children}</> : whenNone;
  }

  const flags: NonEmptyFlagList = typeof props.flag === 'string' ? [props.flag] : props.flag;

  return (
    <AnyFlagEnabled
      flag={flags[0]}
      rest={flags.slice(1)}
      defaultValue={explicitDefault}
      invert={props.invert}
      whenNone={whenNone}
    >
      {props.children}
    </AnyFlagEnabled>
  );
};
